import { FC, ReactNode } from 'react';

export type BadgeVariant =
  | 'success'
  | 'online'
  | 'danger'
  | 'offline'
  | 'warning'
  | 'info'
  | 'neutral'
  | 'cadence';

export interface BadgeProps {
  variant?: BadgeVariant;
  withDot?: boolean;
  pulse?: boolean;
  loadPercent?: number; // Para pílula de cadência/percentual de carga
  children: ReactNode;
  className?: string;
}

const variantStyles: Record<string, { container: string; dot: string }> = {
  success: {
    container:
      'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    dot: 'bg-emerald-500',
  },
  online: {
    container:
      'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    dot: 'bg-emerald-500',
  },
  danger: {
    container: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    dot: 'bg-red-500',
  },
  offline: {
    container: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    dot: 'bg-red-500',
  },
  warning: {
    container:
      'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
    dot: 'bg-amber-500',
  },
  info: {
    container: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20',
    dot: 'bg-sky-500',
  },
  neutral: {
    container: 'bg-surface-subtle text-content-muted border-surface-border',
    dot: 'bg-slate-400 dark:bg-slate-500',
  },
  cadence: {
    container:
      'bg-brand-500/10 text-brand-700 dark:text-brand-300 border-brand-500/20',
    dot: 'bg-brand-500',
  },
};

export const Badge: FC<BadgeProps> = ({
  variant = 'neutral',
  withDot = false,
  pulse = false,
  loadPercent,
  children,
  className = '',
}) => {
  const styles = variantStyles[variant] || variantStyles.neutral;

  // Cor do indicador de carga quando loadPercent é informado
  const getLoadBarColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-brand-500';
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${styles.container} ${className} `}
    >
      {withDot && (
        <span
          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${styles.dot} ${
            pulse ? 'animate-pulse' : ''
          }`}
        />
      )}
      {loadPercent !== undefined && (
        <span className="inline-flex h-1.5 w-10 flex-shrink-0 overflow-hidden rounded-full bg-surface-border">
          <span
            className={`h-full rounded-full transition-all duration-300 ${getLoadBarColor(loadPercent)}`}
            style={{ width: `${Math.min(Math.max(loadPercent, 0), 100)}%` }}
          />
        </span>
      )}
      <span>{children}</span>
    </span>
  );
};
